import React from "react";
import { AlertTriangle, ChevronRight, Loader2, RotateCw } from "lucide-react";

import { RECORD_GROUPS, groupLabel } from "../cmaReportTypes";

/**
 * Results Summary, the page Property Data shows straight after "Start CMA
 * Report": how many records the search found and where they sit.
 *
 * Every count opens its Select Records tab directly. Christian (Monday
 * 13066303151, 12 Oct 2026): "Property results show me 116 but I cant click
 * them. I need to click continue for them to pop up."
 */
const CmaResultsSummary = ({ records, loading, error, rental, statement, onRefine, onRetry, onOpenGroup }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  // A failed search is not "0 results": say so, and offer the retry.
  if (error) {
    return (
      <div className="max-w-3xl" role="alert">
        <h2 className="text-xl font-bold text-gray-900 mb-3">Results Summary</h2>
        <div className="flex items-start gap-3 p-4 rounded-lg border border-amber-200 bg-amber-50 text-sm text-amber-900">
          <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">The search did not finish.</p>
            <p className="mt-0.5">This is not the same as no results. Please try again.</p>
            <button type="button" onClick={onRetry}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-amber-300 font-semibold hover:bg-amber-100">
              <RotateCw className="w-4 h-4" /> Run the search again
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!records) return null;

  const { summary = {}, total = 0, note } = records;
  const one = rental ? "lease" : "transaction";
  const many = rental ? "leases" : "transactions";
  // "1 result is a transaction" / "17 results are transactions".
  const line = (count, where) => (count === 1
    ? `1 result is a ${one} ${where}, which matches your search criteria.`
    : `${count} results are ${many} ${where}, which match your search criteria.`);
  const history = summary.history || 0;
  const listings = summary.listing || 0;

  const lines = [
    {
      key: "history",
      text: history
        ? `${history} result${history === 1 ? " is" : "s are"} the ${rental ? "rental" : "sales"} history for this particular address.`
        : "0 results found for this particular address.",
    },
    { key: "street", text: line(summary.street || 0, "in the same street") },
    { key: "suburb", text: line(summary.suburb || 0, "in the area") },
    ...(statement ? [] : [{
      key: "listing",
      text: listings === 1 ? "1 result is a current listing." : `${listings} results are current listings.`,
    }]),
  ];

  return (
    <div className="max-w-3xl">
      <h2 className="text-xl font-bold text-gray-900 mb-3">Results Summary</h2>
      <p className="text-sm text-gray-800 mb-3">
        <strong>{total} result{total === 1 ? "" : "s"}</strong> returned in total.
      </p>
      <ul className="space-y-1.5 text-sm text-gray-700 mb-4" aria-label="Results by section">
        {lines.map(({ key, text }) => (
          <li key={key}>
            {summary[key] ? (
              <button type="button" onClick={() => onOpenGroup?.(key)}
                className="text-left text-blue-700 hover:underline">
                {text}
              </button>
            ) : text}
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap gap-2 mb-4">
        {RECORD_GROUPS.filter((g) => !(statement && g.key === "listing")).map((group) => (
          <button
            key={group.key}
            type="button"
            onClick={() => onOpenGroup?.(group.key)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-gray-50 border border-gray-100 text-xs text-gray-700 hover:border-gray-300 hover:bg-white"
          >
            {groupLabel(group, rental)}
            <span className="px-1.5 rounded bg-[#1f2a44] text-white font-bold">{summary[group.key] || 0}</span>
            <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
          </button>
        ))}
      </div>

      {total === 0 && (
        <p className="text-sm text-gray-600 mb-3">
          Nothing matched. Try{" "}
          <button type="button" onClick={onRefine} className="font-semibold underline">Refine Search</button>
          {" "}with a wider date range or Include Surrounding, or continue and use Add Property.
        </p>
      )}
      {note && <p className="text-xs text-gray-500">{note}</p>}
    </div>
  );
};

export default CmaResultsSummary;
